import type { DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { OrgId, UserId } from './ids.ts'
import type { JobId } from './Job.ts'
import type { JobRequestId, UnregisteredUser } from './JobRequest.ts'

export enum AgreementType {
    jobMediationContract = 'zmluva-o-sprostredkovani-zamestnania',
    jobMediationAgreement = 'dohoda-o-sprostredkovani-zamestnania',
    privateDataPolicy = 'ochrana-osobnych-udajov',
    internshipMediationContract = 'zmluva-o-spolupraci-staz',
    marketingConsent = 'suhlas-s-marketingovou-komunikaciou',
    audiovisualPhotoConsent = 'suhlas-s-audiovizualnym-a-fotografickym-zaznamom',
    jobApplicationConsent = 'suhlas-pri-prejaveni-zaujmu-o-job',
    selfKnowledgeConsent = 'suhlas-so-sebapoznanim',
    newsletterConsent = 'suhlas-s-odoberanim-newslettera',
    personalDataLeafCommunityConsent = 'suhlas-pre-ucast-v-leaf-komunite',
    personalDataLeafVisionConsent = 'suhlas-s-viziou-leaf',
    cooperationAgreement = 'zmluva-o-spolupraci',
    jobAdvertisingTermsAndConditions = 'obchodne-podmienky-pre-inzerovanie-jobu'
}

export type AgreementId = string

export type Agreement = DocumentWithTimestamps & {
    id: AgreementId
    validFrom: Timestamp
    validTo?: Timestamp
    docUrl?: string
    type: AgreementType
    wording?: string
    fullText?: string
    createdById: string
    createdByMeta: {
        name: string
    }
    note?: string
    yearsConsentValid?: number
}

export const externalConsentTypes = {
    [AgreementType.marketingConsent]: 'marketing',
    [AgreementType.newsletterConsent]: 'newsletter'
}

export type ConsentId = string

export type Consent = DocumentWithTimestamps & {
    id: ConsentId
    orgId: OrgId
    jobId?: JobId
    jobRequestId?: JobRequestId
    agreementId: AgreementId
    agreementType: AgreementType
    accepted: boolean
    givenAt: Timestamp
    givenById?: UserId
    givenBy?: UnregisteredUser
    revokedAt?: Timestamp
    revokedById?: UserId
    revokedBy?: UnregisteredUser
    validUntil?: Timestamp
}

export const agreementTitles: { [key in AgreementType]: string } = {
    [AgreementType.jobMediationContract]: 'Zmluva o sprostredkovaní zamestnania',
    [AgreementType.jobMediationAgreement]: 'Dohoda o sprostredkovaní zamestnania',
    [AgreementType.privateDataPolicy]: 'Ochrana osobných údajov',
    [AgreementType.internshipMediationContract]: 'Zmluva o spolupráci - Stáž',
    [AgreementType.marketingConsent]: 'Súhlas s marketingovou komunikáciou',
    [AgreementType.audiovisualPhotoConsent]: 'Súhlas s audiovizuálnym a fotografickým záznamom',
    [AgreementType.jobApplicationConsent]: 'Súhlas pri prejavení záujmu o job',
    [AgreementType.selfKnowledgeConsent]: 'Súhlas so sebapoznaním',
    [AgreementType.newsletterConsent]: 'Súhlas so odoberaním newslettera',
    [AgreementType.personalDataLeafCommunityConsent]: 'Súhlas pre účasť v komunite',
    [AgreementType.personalDataLeafVisionConsent]: 'Súhlas s víziou',
    [AgreementType.cooperationAgreement]: 'Zmluva o spolupráci',
    [AgreementType.jobAdvertisingTermsAndConditions]: 'Obchodné podmienky'
}

export interface Agreements {
    [AgreementType.jobMediationContract]?: Agreement
    [AgreementType.internshipMediationContract]?: Agreement
    [AgreementType.privateDataPolicy]?: Agreement
    [AgreementType.jobMediationAgreement]?: Agreement
    [AgreementType.marketingConsent]?: Agreement
    [AgreementType.audiovisualPhotoConsent]?: Agreement
    [AgreementType.jobApplicationConsent]?: Agreement
    [AgreementType.selfKnowledgeConsent]?: Agreement
    [AgreementType.newsletterConsent]?: Agreement
    [AgreementType.personalDataLeafCommunityConsent]?: Agreement
    [AgreementType.personalDataLeafVisionConsent]?: Agreement
    [AgreementType.cooperationAgreement]?: Agreement
    [AgreementType.jobAdvertisingTermsAndConditions]?: Agreement
}
